/* eslint-disable */
import { NormalizedPath, RouteType } from './types';

const param = '{param}';

export class UniversalRoute {
  private static readonly SLUG_PATTERN = '[^/]+';
  private static readonly ROUTE_REGEX = new RegExp(`^(|/|((/${UniversalRoute.SLUG_PATTERN})+))$`);

  private readonly matchesExp: RegExp;

  public constructor(
    private readonly path: string,
    public readonly routeType: RouteType,
    public readonly disableRedirects: boolean = false
  ) {
    if (!UniversalRoute.ROUTE_REGEX.test(path)) {
      throw new Error(`Path '${path}' is not a valid route path.`);
    }

    this.matchesExp = new RegExp(
      `^(${this.path}|${this.path.replace(new RegExp(param, 'g'), `(${UniversalRoute.SLUG_PATTERN})`)})$`
    );
  }

  public matches(path: NormalizedPath): boolean {
    return this.matchesExp.test(path.asString());
  }
}

/**
 * Definitive list of routes across both consoles.
 *
 * For the hybrid console to work, both consoles must use the exact same route for equivalent pages.
 * See [Achieving Route Parity](https://quip-amazon.com/QL6xALPBtj2D/Jam-Console-UI-Side-by-side-Design#temp:C:QcG69aded7ce1574916af47aca20).
 */
const ConsoleRoutes: UniversalRoute[] = [
  new UniversalRoute('', RouteType.PolarisOnly),
  new UniversalRoute('/', RouteType.PolarisOnly),
  /* 
    Disable redirects on the /oauth2 route, since users need to complete authentication while on this route before being redirected.
    Both apps redirect the user to another page after authentication has completed on this route, and the hybrid console redirect
    would interfere with this process.
   */
  new UniversalRoute('/oauth2', RouteType.PolarisOnly, /* disableRedirects */ true),
  new UniversalRoute('/signout', RouteType.PolarisOnly),
  new UniversalRoute('/events', RouteType.PolarisOnly),
  new UniversalRoute('/my-events', RouteType.PolarisOnly),
  new UniversalRoute('/events/new', RouteType.PolarisOnly),
  new UniversalRoute(`/events/${param}`, RouteType.PolarisOnly),
  new UniversalRoute(`/events/${param}/challenges`, RouteType.PolarisOnly),
  new UniversalRoute(`/events/${param}/settings`, RouteType.PolarisOnly),
  new UniversalRoute(`/events/${param}/comments`, RouteType.PolarisOnly),
  new UniversalRoute(`/events/${param}/participants`, RouteType.PolarisOnly),
  new UniversalRoute(`/events/${param}/labs`, RouteType.PolarisOnly),
  new UniversalRoute(`/events/${param}/feedback`, RouteType.PolarisOnly),
  new UniversalRoute(`/events/${param}/revisions`, RouteType.PolarisOnly),
  new UniversalRoute(`/events/${param}/report`, RouteType.PolarisOnly),
  new UniversalRoute('/campaigns', RouteType.PolarisOnly),
  new UniversalRoute(`/campaigns/${param}`, RouteType.PolarisOnly),
  new UniversalRoute(`/campaigns/${param}/challenges`, RouteType.PolarisOnly),
  new UniversalRoute(`/campaigns/${param}/settings`, RouteType.PolarisOnly),
  new UniversalRoute(`/campaigns/${param}/advanced-settings`, RouteType.PolarisOnly),
  new UniversalRoute(`/campaigns/${param}/groups/new`, RouteType.PolarisOnly),
  new UniversalRoute(`/campaigns/${param}/groups/${param}`, RouteType.PolarisOnly),
  new UniversalRoute(`/campaigns/${param}/groups`, RouteType.PolarisOnly),
  new UniversalRoute(`/campaigns/${param}/comments`, RouteType.PolarisOnly),
  new UniversalRoute('/challenges', RouteType.PolarisOnly),
  new UniversalRoute('/challenges/snippets', RouteType.PolarisOnly),
  new UniversalRoute(`/challenges/snippets/${param}`, RouteType.PolarisOnly),
  new UniversalRoute('/challenges/boards', RouteType.PolarisOnly),
  new UniversalRoute(`/challenges/boards/${param}`, RouteType.PolarisOnly),
  new UniversalRoute('/challenge-set', RouteType.PolarisOnly),
  new UniversalRoute(`/challenge-set/${param}`, RouteType.PolarisOnly),
  new UniversalRoute(`/challenges/${param}`, RouteType.PolarisOnly),
  new UniversalRoute('/challenges/new', RouteType.PolarisOnly),
  new UniversalRoute(`/challenges/${param}/learning-outcome`, RouteType.PolarisOnly),
  new UniversalRoute(`/challenges/${param}/next-steps`, RouteType.PolarisOnly),
  new UniversalRoute(`/challenges/${param}/tasks`, RouteType.PolarisOnly),
  new UniversalRoute(`/challenges/${param}/tasks/${param}`, RouteType.PolarisOnly),
  new UniversalRoute(`/challenges/${param}/testing`, RouteType.PolarisOnly),
  new UniversalRoute(`/challenges/${param}/collaborators`, RouteType.PolarisOnly),
  new UniversalRoute(`/challenges/${param}/settings`, RouteType.PolarisOnly),
  new UniversalRoute(`/challenges/${param}/wiki`, RouteType.PolarisOnly),
  new UniversalRoute(`/challenges/${param}/facilitator-notes`, RouteType.PolarisOnly),
  new UniversalRoute(`/challenges/${param}/comments`, RouteType.PolarisOnly),
  new UniversalRoute(`/challenges/${param}/issues`, RouteType.PolarisOnly),
  new UniversalRoute(`/challenges/${param}/issues/${param}`, RouteType.PolarisOnly),
  new UniversalRoute(`/challenges/${param}/new-issue`, RouteType.PolarisOnly),
  new UniversalRoute(`/challenges/${param}/feedback`, RouteType.PolarisOnly),
  new UniversalRoute(`/challenges/${param}/assets`, RouteType.PolarisOnly),
  new UniversalRoute(`/challenges/${param}/iam`, RouteType.PolarisOnly),
  new UniversalRoute(`/challenges/${param}/cfn`, RouteType.PolarisOnly),
  new UniversalRoute(`/challenges/${param}/stats`, RouteType.PolarisOnly),
  new UniversalRoute(`/challenges/${param}/revisions`, RouteType.PolarisOnly),
  new UniversalRoute(`/challenges/${param}/translation`, RouteType.PolarisOnly),
  new UniversalRoute(`/challenges/${param}/export`, RouteType.PolarisOnly),
  new UniversalRoute(`/challenges/${param}/help`, RouteType.PolarisOnly),
  new UniversalRoute('/challenge-wiki-example', RouteType.PolarisOnly),
  new UniversalRoute('/reports', RouteType.PolarisOnly),
  new UniversalRoute('/review-challenges', RouteType.PolarisOnly),
  new UniversalRoute('/settings', RouteType.PolarisOnly),
  new UniversalRoute('/labs', RouteType.PolarisOnly),
  new UniversalRoute('/labs/pools', RouteType.PolarisOnly),
  new UniversalRoute('/labs/shutoff', RouteType.PolarisOnly),
  new UniversalRoute('/documentation', RouteType.PolarisOnly),
  new UniversalRoute('/account', RouteType.PolarisOnly),
  new UniversalRoute('/ui-samples', RouteType.PolarisOnly),
];

/**
 * Get the universal route corresponding to the given path.
 */
export function getUniversalRoute(path: NormalizedPath): UniversalRoute | undefined {
  for (const route of ConsoleRoutes) {
    if (route.matches(path)) {
      return route;
    }
  }

  return undefined;
}
